import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useInView } from '@/hooks/useInView';
import {
  ArrowRight, Check,
  ClipboardCheck, Warehouse, Factory, ShieldCheck, RefreshCw,
  Wrench, Ruler, MessageSquareWarning, FileSearch, GraduationCap,
  Rocket, BarChart3, GitBranch, Cpu, Zap,
  Monitor, Tablet, Smartphone, Laptop, ScanLine, Package, Truck, ArrowDown,
} from 'lucide-react';
import {
  IncomingQualityMockup, DigitalStoresMockup, ProductionMockup,
  QualityMockup, ChangeMgmtMockup, MaintenanceMockup,
  CalibrationMockup, ComplaintsMockup, AuditMockup,
  TrainingMockup, NpdApqpMockup, ManagementMockup,
  TraceabilityMockup, IoTMockup, EnergyMockup,
} from '@/components/mockups/FactoryMockups';

const RED = '#E02D45';
const GREEN = '#34D399';

/* ═══════════════════════════════════════════════════════
   APP DATA
   ═══════════════════════════════════════════════════════ */

const apps = [
  {
    id: 'incoming-quality', name: 'Incoming Quality', icon: ClipboardCheck,
    tagline: 'Digitize incoming inspection, supplier approvals, GRN-linked quality checks, and certificate management.',
    bullets: ['ERP integration for GRN linkage', 'Digital incoming quality standards', 'Digital incoming inspection reports', 'Sampling plan guidance', 'Supplier PDI report and MTC management', 'Supplier certificate management'],
    Mockup: IncomingQualityMockup,
  },
  {
    id: 'digital-stores', name: 'Digital Stores', icon: Warehouse,
    tagline: 'Run barcode-based store operations with FIFO enforcement, lot tracking, rack/bin visibility, and real-time inventory status.',
    bullets: ['Auto inward of IQC-pass material', 'Supplier lot and shelf-life tracking', 'Barcode scanning and label-based inventory management', 'Digital store management of racks and bins', 'Scanning-based bin placement', 'FIFO-based material issue', 'Real-time store status', 'Mushin Store App', 'Bin history cards'],
    Mockup: DigitalStoresMockup,
  },
  {
    id: 'production-execution', name: 'Production Execution', icon: Factory,
    tagline: 'Track live production output, rejections, operator performance, and real-time OEE across every shift.',
    bullets: ['Digital material reservation from store', 'Digital hourly, shift, and daily production reports', 'Rejection and red bin reports', 'Cycle-time-based performance rate calculation', 'Runtime-based machine availability', 'Production-based quality rate', 'Real-time OEE calculation', 'Machine startup checksheets', 'Machine maintenance work orders', 'Man x Machine matrix for availability', 'Operator skill matrix', 'Wages to sales tracking'],
    Mockup: ProductionMockup,
  },
  {
    id: 'quality-management', name: 'Quality Management', icon: ShieldCheck,
    tagline: 'Run control plans, inspections, SPC, FMEA, and layered quality discipline across every production stage.',
    bullets: ['Digital MR and document control', 'Master formats, master procedures, work instructions', 'Control plans, MISP, PQCT, PFD, drawing management', 'Operation and process control standards', 'In-process inspection reports', 'Setup / FPA / LPA reports', 'Final inspection and PDI standards and reports', 'Red bin attendance and red bin analysis', 'FMEA management', 'Real-time SPC and run charts', 'E-checksheets and poka-yoke checksheets', 'Dock audit and layered audit', '5S before / after records'],
    Mockup: QualityMockup,
  },
  {
    id: 'change-management', name: 'Change Management', icon: RefreshCw,
    tagline: 'Manage 4M changes, violations, evidence collection, abnormalities, and corrective action workflows.',
    bullets: ['4M change initiation and approval flow', '4M violations tracking', '4M summary boards', '4M work instructions, action plans, descriptions, procedures', '4M evidence collection', 'Retro / suspected / setup evidence capture', 'Abnormality handling and closure'],
    Mockup: ChangeMgmtMockup,
  },
  {
    id: 'maintenance', name: 'Maintenance', icon: Wrench,
    tagline: 'Track machine history, preventive and predictive maintenance, breakdowns, spare control, energy linkage, and asset health.',
    bullets: ['Machine list and machine history cards', 'Tool, die, and mould list with history cards', 'Critical spare part lists', 'Preventive maintenance plan and checksheets', 'Predictive maintenance plan and checksheets', 'Energy monitoring linkage', 'Diesel and energy consumption tracking', 'Energy consumption to sales', 'Breakdown registers', 'Machine-wise consumption and PF tracking', 'MTTR and MTBF analysis'],
    Mockup: MaintenanceMockup,
  },
  {
    id: 'calibration-lab', name: 'Calibration Lab', icon: Ruler,
    tagline: 'Manage instruments, gauges, calibration schedules, agency records, certificates, and MSA reports.',
    bullets: ['Instrument master', 'Gauge master', 'Calibration agency management', 'Calibration history tracking', 'Calibration certificate management', 'Instrument and gauge MSA reports'],
    Mockup: CalibrationMockup,
  },
  {
    id: 'complaints-capa', name: 'Complaints & CAPA', icon: MessageSquareWarning,
    tagline: 'Handle customer complaints, quality alerts, 8D CAPA, feedback surveys, and Pareto-driven closure discipline.',
    bullets: ['Customer complaint register', 'Complaint and quality alerts', '8D CAPA management and analysis', 'Customer feedback survey', 'PTDB (Problem-To-Daily-Board)', 'Complaint tracker for repeat and non-repeat complaints', 'Global customer audit, delivery default, and quality alerts', 'Pareto analysis'],
    Mockup: ComplaintsMockup,
  },
  {
    id: 'audit-management', name: 'Audit Management', icon: FileSearch,
    tagline: 'Digitize internal, process, product, dock, and layered audits with finding tracking and closure workflows.',
    bullets: ['Internal audit planning and execution', 'Product audit workflows', 'Process audit management', 'Dock audit', 'Layered audits', 'Audit findings and closure tracking'],
    Mockup: AuditMockup,
  },
  {
    id: 'training-dojo', name: 'Training / DOJO', icon: GraduationCap,
    tagline: 'Run training plans, skill matrices, digital evaluations, VR-based training, and digital dojo programmes.',
    bullets: ['Contractual operator and employee lists', 'Skill matrix and gap analysis', 'TNI (Training Need Identification)', 'Training plan creation and scheduling', 'Skill evaluation plans', 'Digital test papers with auto evaluation', 'Induction, OJT, effectiveness, and skill evaluation tests', 'Employee training history', 'Attendance management', 'VR-based training videos', 'Digital dojo'],
    Mockup: TrainingMockup,
  },
  {
    id: 'npd-apqp', name: 'NPD / APQP', icon: Rocket,
    tagline: 'Track APQP matrices, PPAP release, drawings, project milestones, and new product development timelines.',
    bullets: ['Coherent APQP matrix and APQP time plan', 'Drawing management', 'Project management', 'APQP activity status tracker', 'On-time and delayed activity visibility', 'Digital PPAP release', 'Project tracker'],
    Mockup: NpdApqpMockup,
  },
  {
    id: 'management-dashboards', name: 'Management Dashboards', icon: BarChart3,
    tagline: 'Executive visibility into KPIs, OEE, PPM, rejection trends, department health, and plant-wide reporting.',
    bullets: ['MIS and KPI tracking', 'OEE analysis', 'PPM tracking', 'Rejection Pareto analysis', 'Advanced management dashboards', 'Department health status', 'Mushin Alerts App', 'Email summary and reports', 'Notifications and alerts'],
    Mockup: ManagementMockup,
  },
  {
    id: 'traceability', name: 'End-to-End Traceability', icon: GitBranch,
    tagline: 'Connect every module into one traceability chain. Raw material to dispatch, with digital genealogy and full forward/backward trace.',
    bullets: ['Coherent interconnection of all modules', 'Raw material to dispatch traceability', 'Barcode, part, bin, lot, and stage-wise traceability', 'Machine-linked events', 'Inspection-linked events', 'Forward trace and backward trace', 'Digital genealogy'],
    Mockup: TraceabilityMockup,
  },
  {
    id: 'iot-machine-data', name: 'IoT & Machine Data', icon: Cpu,
    tagline: 'Capture PLC data, sensor inputs, live dashboards, Andon alerts, OEE, runtime, alarms, and operator-linked production intelligence.',
    bullets: ['PLC-based machine production data collection', 'Sensor and barcode scanning at every stage', 'Live shopfloor dashboards', 'Shopfloor Andon alerts', 'Advanced analytics', 'OEE, runtime, and downtime tracking', 'Alarms and poka-yoke health monitoring', 'Biometric-linked or operator-linked production intelligence'],
    Mockup: IoTMockup,
  },
  {
    id: 'energy-monitoring', name: 'Energy Monitoring', icon: Zap,
    tagline: 'Track energy and diesel consumption, machine-wise usage, cost-to-output linkage, and efficiency trends.',
    bullets: ['Energy consumption tracking', 'Diesel consumption tracking', 'Machine-wise energy usage', 'Cost-to-output linkage', 'Trend analysis and alerts'],
    Mockup: EnergyMockup,
  },
];

const packages = [
  {
    name: 'Lite',
    desc: 'For smaller deployments or single-department digitisation.',
    highlighted: false,
    features: { 'Users': 'Up to 10', 'Connected Apps': '1', 'Inter-Plant Connectivity': false, 'Mushin App': false, 'Notifications & Alerts': 'Basic', 'Dashboards': 'Basic', 'ERP / Third-Party Integration': false, 'Role-Based Access': true, 'Dedicated Rollout Support': false },
  },
  {
    name: 'Pro',
    desc: 'For full plant digitisation across multiple functions.',
    highlighted: true,
    features: { 'Users': 'Up to 30', 'Connected Apps': '10', 'Inter-Plant Connectivity': false, 'Mushin App': true, 'Notifications & Alerts': 'Advanced', 'Dashboards': 'Advanced', 'ERP / Third-Party Integration': true, 'Role-Based Access': true, 'Dedicated Rollout Support': true },
  },
  {
    name: 'Enterprise',
    desc: 'For multi-plant groups with deeper integration needs.',
    highlighted: false,
    features: { 'Users': 'Up to 75', 'Connected Apps': 'All', 'Inter-Plant Connectivity': true, 'Mushin App': true, 'Notifications & Alerts': 'Advanced + Custom', 'Dashboards': 'Advanced + Custom', 'ERP / Third-Party Integration': true, 'Role-Based Access': true, 'Dedicated Rollout Support': true },
  },
];

const featureRows = [
  'Users', 'Connected Apps', 'Inter-Plant Connectivity', 'Mushin App',
  'Notifications & Alerts', 'Dashboards', 'ERP / Third-Party Integration',
  'Role-Based Access', 'Dedicated Rollout Support',
];

const traceSteps = [
  { label: 'Raw Material', sub: 'Inward & tagging', icon: Package },
  { label: 'IQC', sub: 'Inspection & sampling', icon: ClipboardCheck },
  { label: 'Stores', sub: 'FIFO & bin placement', icon: Warehouse },
  { label: 'Production', sub: 'Line execution', icon: Factory },
  { label: 'In-Process QC', sub: 'Quality checkpoints', icon: ScanLine },
  { label: 'Final QC', sub: 'Sign-off & release', icon: ShieldCheck },
  { label: 'Dispatch', sub: 'Shipment & records', icon: Truck },
];

/* ═══════════════════════════════════════════════════════
   SHARED COMPONENTS
   ═══════════════════════════════════════════════════════ */

function Section({ children, className = '', id }) {
  const [ref, inView] = useInView();
  return (
    <section ref={ref} id={id} className={`${inView ? 'animate-fade-in-up' : 'opacity-0'} ${className}`}>
      {children}
    </section>
  );
}

function TypewriterLine({ text, visible, startDelay = 0, highlightColor = null }) {
  return (
    <div className="overflow-hidden">
      <span className="inline-block">
        {text.split('').map((char, i) => (
          <span
            key={i}
            className={`inline-block typewriter-char ${visible ? 'typewriter-char-visible' : ''}`}
            style={{ transitionDelay: `${startDelay + i * 70}ms`, color: highlightColor || undefined }}
          >
            {char === ' ' ? '\u00A0' : char}
          </span>
        ))}
      </span>
    </div>
  );
}

function FeatureCell({ value }) {
  if (value === true) return <Check className="w-4 h-4 text-emerald-400 mx-auto" strokeWidth={2.5} />;
  if (value === false) return <span className="text-white/15">{'\u2013'}</span>;
  return <span className="text-white/70 text-sm font-medium">{value}</span>;
}

function AppDetailSection({ app, index }) {
  const isEven = index % 2 === 0;
  const Icon = app.icon;
  const MockupComponent = app.Mockup;

  return (
    <Section id={app.id} className="py-20 lg:py-28 border-t border-white/[0.04]">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start ${!isEven ? 'lg:[direction:rtl]' : ''}`}>
          {/* Text */}
          <div className={`${!isEven ? 'lg:[direction:ltr]' : ''} pt-2`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center border" style={{ backgroundColor: `${RED}12`, borderColor: `${RED}25` }}>
                <Icon className="w-5 h-5" style={{ color: RED }} strokeWidth={1.7} />
              </div>
              <p className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: RED }}>Factory OS App</p>
            </div>
            <h3 className="text-2xl sm:text-3xl lg:text-4xl font-black uppercase tracking-[-0.02em] text-white leading-[1]" data-testid={`app-detail-${app.id}`}>
              {app.name}
            </h3>
            <p className="mt-4 text-[15px] text-white/45 leading-[1.7] font-medium max-w-md">
              {app.tagline}
            </p>
            <ul className="mt-6 space-y-2">
              {app.bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                  <span className="text-sm text-white/55 font-medium">{b}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Dashboard Mockup */}
          <div className={!isEven ? 'lg:[direction:ltr]' : ''}>
            <MockupComponent />
          </div>
        </div>
      </div>
    </Section>
  );
}

/* ═══════════════════════════════════════════════════════
   CROSS-PLATFORM SECTION
   ═══════════════════════════════════════════════════════ */

function CrossPlatformSection() {
  const [ref, inView] = useInView();
  return (
    <section ref={ref} className={`py-24 lg:py-36 bg-[#060606] border-t border-white/[0.04] overflow-hidden ${inView ? 'animate-fade-in-up' : 'opacity-0'}`} data-testid="cross-platform-section">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
        <div className="text-center mb-16">
          <p className="text-sm font-bold uppercase tracking-[0.25em] mb-6" style={{ color: RED }}>Built for Every Screen</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-[-0.02em] text-white leading-[1]">
            One Operating System.<br />Every Device.
          </h2>
          <p className="mt-5 text-base text-white/40 max-w-xl mx-auto font-medium leading-[1.7]">
            Plant floor, supervisor desk, or management boardroom. Mushin works across desktop, laptop, tablet, and mobile.
          </p>
        </div>

        {/* Device Mockups */}
        <div className="relative flex items-end justify-center gap-4 sm:gap-6 lg:gap-8 min-h-[300px] sm:min-h-[350px] lg:min-h-[400px]">
          {/* Desktop Monitor */}
          <div className="hidden lg:block relative" style={{ width: '420px' }}>
            <div className="rounded-xl border-2 border-white/[0.1] bg-[#0A0A0A] overflow-hidden">
              <div className="flex items-center gap-1.5 px-3 py-2 bg-white/[0.03] border-b border-white/[0.06]">
                <div className="flex gap-1"><div className="w-2 h-2 rounded-full bg-white/10" /><div className="w-2 h-2 rounded-full bg-white/10" /><div className="w-2 h-2 rounded-full bg-white/10" /></div>
                <span className="text-[9px] text-white/20 font-mono ml-2">app.mushinlabs.com/dashboard</span>
              </div>
              {/* Screen content - Executive Dashboard */}
              <div className="p-3">
                <div className="grid grid-cols-4 gap-2 mb-3">
                  {[{l:'OEE',v:'84%',c:GREEN},{l:'PPM',v:'126',c:'#FBBF24'},{l:'OTIF',v:'97.8%',c:GREEN},{l:'Rej.',v:'1.2%',c:GREEN}].map(k => (
                    <div key={k.l} className="p-2 rounded-md bg-white/[0.03] border border-white/[0.04]">
                      <p className="text-[7px] text-white/20 uppercase">{k.l}</p>
                      <p className="text-[11px] font-black text-white/60">{k.v}</p>
                    </div>
                  ))}
                </div>
                <div className="h-14 bg-white/[0.02] rounded-md border border-white/[0.04] p-2">
                  <svg viewBox="0 0 200 30" className="w-full h-full">
                    <polyline fill="none" stroke={RED} strokeWidth="1.5" opacity="0.5" points="5,25 30,22 55,18 80,20 105,14 130,12 155,10 180,8" />
                    <polyline fill="none" stroke={GREEN} strokeWidth="1" opacity="0.3" strokeDasharray="2,2" points="5,15 180,15" />
                  </svg>
                </div>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {[1,2,3].map(i => (
                    <div key={i} className="h-5 rounded bg-white/[0.02] border border-white/[0.03]" />
                  ))}
                </div>
              </div>
            </div>
            {/* Monitor Stand */}
            <div className="mx-auto mt-0 flex flex-col items-center">
              <div className="w-16 h-6 bg-white/[0.04] border-x-2 border-white/[0.06]" />
              <div className="w-28 h-2 bg-white/[0.04] rounded-b-lg border border-t-0 border-white/[0.06]" />
            </div>
          </div>

          {/* Laptop */}
          <div className="relative" style={{ width: 'min(320px, 45vw)' }}>
            <div className="rounded-t-lg border-2 border-b-0 border-white/[0.1] bg-[#0A0A0A] overflow-hidden">
              <div className="flex items-center gap-1 px-2 py-1.5 bg-white/[0.03] border-b border-white/[0.06]">
                <div className="flex gap-1"><div className="w-1.5 h-1.5 rounded-full bg-white/10" /><div className="w-1.5 h-1.5 rounded-full bg-white/10" /><div className="w-1.5 h-1.5 rounded-full bg-white/10" /></div>
              </div>
              <div className="p-2">
                <div className="grid grid-cols-3 gap-1.5 mb-2">
                  {[{l:'Output',v:'2,847'},{l:'Rej',v:'1.2%'},{l:'OEE',v:'82%'}].map(k => (
                    <div key={k.l} className="p-1.5 rounded bg-white/[0.03] border border-white/[0.04]">
                      <p className="text-[6px] text-white/20 uppercase">{k.l}</p>
                      <p className="text-[9px] font-black text-white/60">{k.v}</p>
                    </div>
                  ))}
                </div>
                <div className="flex gap-1 mb-2">
                  {[85,92,88,76,91,94].map((v,i) => (
                    <div key={i} className="flex-1 flex flex-col justify-end h-10 rounded-sm" style={{backgroundColor:'rgba(255,255,255,0.02)'}}>
                      <div className="rounded-t-sm" style={{height:`${v*0.4}px`, backgroundColor: v >= 90 ? `${GREEN}40` : `${RED}30`}} />
                    </div>
                  ))}
                </div>
                {[1,2].map(i => <div key={i} className="h-3 mb-1 rounded bg-white/[0.02] border border-white/[0.03]" />)}
              </div>
            </div>
            {/* Laptop Base */}
            <div className="w-[115%] -ml-[7.5%] h-3 bg-white/[0.04] rounded-b-xl border border-t-2 border-white/[0.08]" />
          </div>

          {/* Tablet */}
          <div className="relative hidden sm:block" style={{ width: 'min(180px, 25vw)' }}>
            <div className="rounded-xl border-2 border-white/[0.1] bg-[#0A0A0A] overflow-hidden p-1">
              <div className="p-2">
                <div className="flex gap-1 mb-2">
                  <div className="flex-1 p-1.5 rounded bg-white/[0.03] border border-white/[0.04]">
                    <p className="text-[5px] text-white/20">PM Compliance</p>
                    <p className="text-[8px] font-black text-white/60">94%</p>
                  </div>
                  <div className="flex-1 p-1.5 rounded bg-white/[0.03] border border-white/[0.04]">
                    <p className="text-[5px] text-white/20">MTBF</p>
                    <p className="text-[8px] font-black text-white/60">187h</p>
                  </div>
                </div>
                {[1,2,3,4].map(i => (
                  <div key={i} className="flex items-center gap-1 mb-1">
                    <div className="w-1.5 h-1.5 rounded-full" style={{backgroundColor: i === 3 ? RED : GREEN, opacity: 0.5}} />
                    <div className="flex-1 h-2.5 rounded bg-white/[0.03]" />
                  </div>
                ))}
                <div className="mt-2 h-8 rounded bg-white/[0.02] border border-white/[0.03]" />
              </div>
            </div>
          </div>

          {/* Phone */}
          <div className="relative" style={{ width: 'min(100px, 18vw)' }}>
            <div className="rounded-2xl border-2 border-white/[0.1] bg-[#0A0A0A] overflow-hidden p-1">
              {/* Notch */}
              <div className="mx-auto w-10 h-1.5 bg-white/[0.06] rounded-full mb-1" />
              <div className="p-1.5">
                <div className="p-1.5 rounded bg-white/[0.03] border border-white/[0.04] mb-1.5">
                  <p className="text-[5px] text-white/20 uppercase">OEE</p>
                  <p className="text-[9px] font-black text-white/70">82.4%</p>
                </div>
                {[1,2,3].map(i => (
                  <div key={i} className="flex items-center gap-1 mb-1">
                    <div className="w-1 h-1 rounded-full" style={{backgroundColor: i === 2 ? '#FBBF24' : GREEN, opacity: 0.5}} />
                    <div className="flex-1 h-2 rounded bg-white/[0.03]" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Device Labels */}
        <div className="flex items-center justify-center gap-8 sm:gap-12 mt-10">
          {[
            { icon: Monitor, label: 'Desktop', hide: 'hidden lg:flex' },
            { icon: Laptop, label: 'Laptop', hide: 'flex' },
            { icon: Tablet, label: 'Tablet', hide: 'hidden sm:flex' },
            { icon: Smartphone, label: 'Mobile', hide: 'flex' },
          ].map(d => (
            <div key={d.label} className={`${d.hide} items-center gap-2`}>
              <d.icon className="w-4 h-4 text-white/25" strokeWidth={1.5} />
              <span className="text-xs text-white/30 font-medium">{d.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════
   PREMIUM TRACEABILITY SECTION
   ═══════════════════════════════════════════════════════ */

function TraceabilityHighlight() {
  const [ref, inView] = useInView();
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const t = setInterval(() => {
      setActiveStep(prev => (prev + 1) % traceSteps.length);
    }, 1200);
    return () => clearInterval(t);
  }, [inView, setActiveStep]);

  return (
    <section ref={ref} className={`py-28 lg:py-40 bg-[#060606] border-t border-white/[0.04] overflow-hidden ${inView ? 'animate-fade-in-up' : 'opacity-0'}`} data-testid="traceability-highlight">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Text */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: RED }} />
              <p className="text-xs font-bold uppercase tracking-[0.25em]" style={{ color: RED }}>Product Differentiator</p>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-[-0.02em] text-white leading-[1]">
              End-to-End<br />Traceability
            </h2>
            <p className="mt-6 text-base text-white/40 leading-[1.7] font-medium max-w-lg">
              Every module in Mushin Factory OS feeds into one traceability chain. From the raw material tag to the dispatch record, every event is linked, timestamped, and traceable.
            </p>
            <ul className="mt-8 space-y-3">
              {[
                'Raw material to finished good, in one chain',
                'Barcode, lot, bin, stage, and inspection linkage',
                'Machine-linked and operator-linked events',
                'Full forward trace and backward trace',
                'Digital genealogy for every part produced',
              ].map((b, i) => (
                <li key={i} className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                  <span className="text-sm text-white/55 font-medium">{b}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Animated Trace Chain */}
          <div>
            <div className="relative">
              {/* Vertical chain */}
              <div className="space-y-1">
                {traceSteps.map((step, i) => {
                  const Icon = step.icon;
                  const isActive = i <= activeStep;
                  const isCurrent = i === activeStep;
                  return (
                    <div key={i} className="flex items-center gap-4">
                      {/* Connector line + dot */}
                      <div className="flex flex-col items-center w-8">
                        {i > 0 && (
                          <div className="w-px h-4 transition-colors duration-500" style={{ backgroundColor: isActive ? `${RED}60` : 'rgba(255,255,255,0.06)' }} />
                        )}
                        <div className={`w-4 h-4 rounded-full flex items-center justify-center transition-all duration-500 ${isCurrent ? 'scale-125' : ''}`}
                          style={{
                            backgroundColor: isActive ? `${RED}20` : 'rgba(255,255,255,0.03)',
                            border: `2px solid ${isActive ? RED : 'rgba(255,255,255,0.08)'}`,
                            boxShadow: isCurrent ? `0 0 12px ${RED}40` : 'none',
                          }}>
                          <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: isActive ? RED : 'rgba(255,255,255,0.15)' }} />
                        </div>
                        {i < traceSteps.length - 1 && (
                          <div className="w-px h-4 transition-colors duration-500" style={{ backgroundColor: i < activeStep ? `${RED}60` : 'rgba(255,255,255,0.06)' }} />
                        )}
                      </div>
                      {/* Content */}
                      <div className={`flex-1 flex items-center gap-4 py-3 px-4 rounded-xl transition-all duration-500 ${isCurrent ? 'bg-white/[0.04] border border-white/[0.08]' : 'border border-transparent'}`}>
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-500"
                          style={{
                            backgroundColor: isActive ? `${RED}12` : 'rgba(255,255,255,0.02)',
                            border: `1px solid ${isActive ? `${RED}25` : 'rgba(255,255,255,0.04)'}`,
                          }}>
                          <Icon className="w-5 h-5 transition-colors duration-500" style={{ color: isActive ? RED : 'rgba(255,255,255,0.15)' }} strokeWidth={1.7} />
                        </div>
                        <div>
                          <p className={`text-sm font-bold transition-colors duration-500 ${isActive ? 'text-white/80' : 'text-white/20'}`}>{step.label}</p>
                          <p className={`text-xs transition-colors duration-500 ${isActive ? 'text-white/35' : 'text-white/10'}`}>{step.sub}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════
   PAGE
   ═══════════════════════════════════════════════════════ */

export default function MushinControlPage() {
  const [heroVisible, setHeroVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setHeroVisible(true), 500);
    return () => clearTimeout(t);
  }, [setHeroVisible]);

  const scrollToApp = useCallback((id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  return (
    <div>
      {/* 1. HERO — Typewriter Reveal */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 bg-[#060606] overflow-hidden" data-testid="factory-os-hero">
        <div className="absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: `linear-gradient(${RED}30 1px, transparent 1px), linear-gradient(90deg, ${RED}30 1px, transparent 1px)`,
          backgroundSize: '80px 80px'
        }} />
        <div className="absolute top-0 right-0 w-[600px] h-[400px] rounded-full blur-[200px]" style={{ backgroundColor: `${RED}06` }} />

        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 relative z-10">
          <div className={`transition-all duration-[1200ms] ease-out ${heroVisible ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: '100ms' }}>
            <div className="flex items-center gap-3 mb-8">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: RED }} />
              <p className="text-xs font-bold uppercase tracking-[0.25em]" style={{ color: RED }}>Mushin Factory OS</p>
            </div>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-[4.5rem] font-black uppercase tracking-[-0.03em] text-white leading-[0.92] max-w-4xl" data-testid="factory-os-heading">
            <TypewriterLine text="The" visible={heroVisible} startDelay={400} />
            <TypewriterLine text="Operating System" visible={heroVisible} startDelay={700} highlightColor={RED} />
            <TypewriterLine text="for Factory" visible={heroVisible} startDelay={1900} highlightColor={RED} />
            <TypewriterLine text="Operations" visible={heroVisible} startDelay={2700} />
          </h1>

          <div className={`transition-all duration-[1400ms] ease-out ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '3800ms' }}>
            <div className="mt-10 h-[3px] w-16 hero-accent" style={{ backgroundColor: RED, animationDelay: '3900ms' }} />
            <p className="mt-8 text-lg text-white/50 max-w-2xl leading-[1.7] font-medium">
              One connected system for quality, production, traceability, audits, maintenance, and more. Built to digitize every critical function on the plant floor.
            </p>
          </div>

          <div className={`mt-10 flex flex-wrap gap-4 transition-all duration-[1400ms] ease-out ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '4200ms' }}>
            <Link to="/contact" className="text-white px-8 py-3.5 rounded-full font-bold text-sm inline-flex items-center gap-2 transition-all duration-300 hover:shadow-xl" style={{ backgroundColor: RED }} data-testid="factory-os-demo-cta">
              Book a Demo <ArrowRight className="w-4 h-4" />
            </Link>
            <a href="#apps" onClick={(e) => { e.preventDefault(); document.getElementById('apps')?.scrollIntoView({ behavior: 'smooth' }); }} className="border-2 border-white/15 hover:border-white/30 text-white/50 hover:text-white px-8 py-3.5 rounded-full font-bold text-sm transition-all duration-300">
              Explore Apps
            </a>
          </div>
        </div>

        <div className={`absolute bottom-6 left-1/2 -translate-x-1/2 transition-all duration-1000 ${heroVisible ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: '4800ms' }}>
          <ArrowDown className="w-5 h-5 text-white/15 animate-bounce" />
        </div>
      </section>

      {/* 2. APP GRID */}
      <Section className="py-20 lg:py-28 bg-[#0A0A0A] border-t border-white/[0.04]" id="apps">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="max-w-3xl mb-14">
            <p className="text-sm font-bold uppercase tracking-[0.25em] mb-6" style={{ color: RED }}>Factory Apps</p>
            <h2 className="text-3xl sm:text-4xl font-black uppercase tracking-[-0.02em] text-white leading-[1.05]">
              One Operating System.<br />Modular Apps for Every Function.
            </h2>
            <p className="mt-5 text-base text-white/40 max-w-xl leading-[1.7] font-medium">
              Mushin Factory OS is made up of integrable apps that digitize the entire plant. Click any app to explore its full scope.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {apps.map((app) => {
              const Icon = app.icon;
              return (
                <button
                  key={app.id}
                  onClick={() => scrollToApp(app.id)}
                  className="group text-left bg-white/[0.03] border border-white/[0.06] rounded-xl p-4 hover:bg-white/[0.06] hover:border-[#E02D45]/25 transition-all duration-300 hover:-translate-y-0.5 cursor-pointer"
                  data-testid={`app-nav-${app.id}`}
                >
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-3 border" style={{ backgroundColor: `${RED}10`, borderColor: `${RED}20` }}>
                    <Icon className="w-[18px] h-[18px]" style={{ color: RED }} strokeWidth={1.7} />
                  </div>
                  <p className="text-[13px] font-bold text-white/80 leading-snug">{app.name}</p>
                  <p className="mt-1 text-[11px] text-white/25 font-medium group-hover:text-white/40 transition-colors">View details</p>
                </button>
              );
            })}
          </div>
        </div>
      </Section>

      {/* 3. DETAILED APP SECTIONS */}
      <div className="bg-[#060606]">
        {apps.map((app, i) => (
          <AppDetailSection key={app.id} app={app} index={i} />
        ))}
      </div>

      {/* 4. PREMIUM TRACEABILITY HIGHLIGHT */}
      <TraceabilityHighlight />

      {/* 5. CROSS-PLATFORM */}
      <CrossPlatformSection />

      {/* 6. PACKAGES */}
      <Section className="py-24 lg:py-36 bg-[#0A0A0A] border-t border-white/[0.04]" id="packages">
        <div className="max-w-[1200px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="text-center mb-16">
            <p className="text-sm font-bold uppercase tracking-[0.25em] mb-6" style={{ color: RED }}>Packages</p>
            <h2 className="text-3xl sm:text-4xl font-black uppercase tracking-[-0.02em] text-white leading-[1.05]">
              Choose Your Factory OS Package
            </h2>
            <p className="mt-4 text-base text-white/40 max-w-lg mx-auto font-medium">
              Start with what you need. Scale as your plant grows.
            </p>
          </div>

          {/* Mobile */}
          <div className="lg:hidden space-y-6">
            {packages.map((pkg, i) => (
              <div key={i} className={`rounded-2xl p-6 border ${pkg.highlighted ? 'border-[#E02D45]/30 bg-[#E02D45]/[0.04]' : 'border-white/[0.06] bg-white/[0.02]'}`}>
                {pkg.highlighted && <p className="text-[10px] font-bold uppercase tracking-[0.2em] mb-3" style={{ color: RED }}>Most Popular</p>}
                <h3 className="text-2xl font-black uppercase text-white">{pkg.name}</h3>
                <p className="mt-2 text-sm text-white/40 font-medium">{pkg.desc}</p>
                <div className="mt-6 space-y-3">
                  {featureRows.map((feat, j) => (
                    <div key={j} className="flex justify-between items-center py-1.5 border-b border-white/[0.04]">
                      <span className="text-xs text-white/40 font-medium">{feat}</span>
                      <FeatureCell value={pkg.features[feat]} />
                    </div>
                  ))}
                </div>
                <div className="mt-6">
                  <Link to="/contact" className={`w-full flex justify-center py-3 rounded-full font-bold text-sm transition-colors ${pkg.highlighted ? 'text-white' : 'text-white/70 border border-white/15 hover:border-white/30'}`} style={pkg.highlighted ? { backgroundColor: RED } : {}}>
                    Talk to Sales
                  </Link>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop */}
          <div className="hidden lg:block">
            <div className="rounded-2xl border border-white/[0.06] overflow-hidden">
              <div className="grid grid-cols-4 border-b border-white/[0.06]">
                <div className="p-6 bg-white/[0.02]" />
                {packages.map((pkg, i) => (
                  <div key={i} className={`p-6 text-center ${pkg.highlighted ? 'bg-[#E02D45]/[0.04] border-x border-[#E02D45]/15' : 'bg-white/[0.02]'}`}>
                    {pkg.highlighted && <p className="text-[10px] font-bold uppercase tracking-[0.2em] mb-2" style={{ color: RED }}>Most Popular</p>}
                    <h3 className="text-xl font-black uppercase text-white">{pkg.name}</h3>
                    <p className="mt-1.5 text-xs text-white/35 font-medium">{pkg.desc}</p>
                  </div>
                ))}
              </div>
              {featureRows.map((feat, j) => (
                <div key={j} className="grid grid-cols-4 border-b border-white/[0.04] last:border-b-0">
                  <div className="p-4 pl-6 flex items-center">
                    <span className="text-sm text-white/50 font-medium">{feat}</span>
                  </div>
                  {packages.map((pkg, i) => (
                    <div key={i} className={`p-4 flex items-center justify-center ${pkg.highlighted ? 'bg-[#E02D45]/[0.02] border-x border-[#E02D45]/8' : ''}`}>
                      <FeatureCell value={pkg.features[feat]} />
                    </div>
                  ))}
                </div>
              ))}
              <div className="grid grid-cols-4 border-t border-white/[0.06]">
                <div className="p-6" />
                {packages.map((pkg, i) => (
                  <div key={i} className={`p-6 flex justify-center ${pkg.highlighted ? 'bg-[#E02D45]/[0.04] border-x border-[#E02D45]/15' : ''}`}>
                    <Link to="/contact" className={`px-8 py-2.5 rounded-full font-bold text-sm transition-all duration-300 ${pkg.highlighted ? 'text-white hover:shadow-lg' : 'text-white/60 border border-white/15 hover:border-white/30 hover:text-white'}`} style={pkg.highlighted ? { backgroundColor: RED } : {}}>
                      Talk to Sales
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* 7. FINAL CTA */}
      <section className="py-28 lg:py-40 relative overflow-hidden bg-[#060606] border-t border-white/[0.04]" data-testid="factory-os-cta">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full blur-[150px]" style={{ backgroundColor: `${RED}06` }} />
        <div className="max-w-4xl mx-auto px-6 sm:px-10 lg:px-16 text-center relative z-10">
          <p className="text-sm font-bold uppercase tracking-[0.25em] mb-8" style={{ color: RED }}>Get Started</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-[-0.02em] text-white leading-[1]">
            Bring Every Plant Function<br />Into One Operating System
          </h2>
          <p className="mt-6 text-white/35 text-lg max-w-lg mx-auto font-medium leading-[1.7]">
            Start with the apps you need. Connect more as your factory grows. One digital backbone for the entire plant.
          </p>
          <div className="mt-10 flex flex-wrap gap-4 justify-center">
            <Link to="/contact" className="text-white px-10 py-4 rounded-full font-bold text-base inline-flex items-center gap-2.5 transition-all duration-300 hover:shadow-xl" style={{ backgroundColor: RED }} data-testid="factory-os-final-cta">
              Book a Demo <ArrowRight className="w-5 h-5" />
            </Link>
            <a href="mailto:sales@mushinlabs.com" className="border-2 border-white/15 hover:border-white/30 text-white/45 hover:text-white px-10 py-4 rounded-full font-bold text-base transition-all duration-300">
              Talk to Sales
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
